import React from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Fingerprint, Camera, User, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const getBiometricTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'fingerprint': return <Fingerprint className="h-4 w-4" />;
    case 'face': return <Camera className="h-4 w-4" />;
    case 'card': return <CreditCard className="h-4 w-4" />;
    case 'user_info':
    case 'user': return <User className="h-4 w-4" />;
    default: return <Database className="h-4 w-4" />;
  }
};

const BiometricDetailsModal = ({ open, onOpenChange, selectedItemDetails, biometricDetails }) => {
  const { t } = useTranslation();
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('biometricComp.detailsTitle')}</DialogTitle>
        <DialogDescription>{t('biometricComp.regNumberLabel')}: {selectedItemDetails?.registration_number}</DialogDescription>
      </DialogHeader>

      {selectedItemDetails && (
        <div className="space-y-6 py-4">
          {Object.entries(biometricDetails).map(([terminal, data]) => (
            <Card key={terminal}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  {t('biometricComp.terminalPrefix', { name: terminal })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Fingerprint className="h-4 w-4" />
                      {t('biometricComp.bioData', { count: data.biometric?.length || 0 })}
                    </h4>
                    <div className="space-y-2">
                      {data.biometric?.map((item, idx) => (
                        <div key={idx} className="p-2 bg-muted/50 rounded border text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getBiometricTypeIcon(item.biometric_type)}
                              <span className="font-medium">{item.biometric_type}</span>
                              {item.finger_id && <span className="text-muted-foreground">{t('biometricComp.finger', { id: item.finger_id })}</span>}
                            </div>
                            <Badge variant={item.is_valid ? 'default' : 'destructive'}>
                              {item.is_valid ? t('biometricComp.valid') : t('biometricComp.invalid')}
                            </Badge>
                          </div>
                          {item.template_size && (
                            <div className="text-xs text-muted-foreground mt-1">{t('biometricComp.sizeBytes', { size: item.template_size })}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      {t('biometricComp.photos', { count: data.photos?.length || 0 })}
                    </h4>
                    <div className="space-y-2">
                      {data.photos?.map((photo, idx) => (
                        <div key={idx} className="p-2 bg-muted/50 rounded border text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Camera className="h-4 w-4" />
                              <span className="font-medium">{t('biometricComp.photoNum', { id: photo.id })}</span>
                            </div>
                            <Badge variant={photo.is_current ? 'default' : 'secondary'}>
                              {photo.is_current ? t('biometricComp.current') : t('biometricComp.old')}
                            </Badge>
                          </div>
                          {photo.photo_size && (
                            <div className="text-xs text-muted-foreground mt-1">{t('biometricComp.sizeBytes', { size: photo.photo_size })}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end pt-4 border-t">
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default BiometricDetailsModal;
